<template>
  <div class="ajax-select" :class="{open: inputIsFocused}">
    <input
      type="text" v-model="input"
      :disabled="disabled" :class="propClass" :placeholder="placeholderText" size="1"
      @keydown="onTyping" @focusin="onFocusIn" @focusout="onFocusOut"
    >
    <div class="x" v-if="clearable" @click="clear">
      <svgicon src="icon/close.svg" :height="12" :width="12"/>
    </div>
    <div class="select-box">
      <div class="select-row" v-if="items.length > 0 && inputIsFocused" v-for="(item, index) of items"
           :key="`e_${item.id}`" :class="{focused: index === focused}" :style="selectRowStyle"
           @mouseenter="setFocused(index)" @click="onSelection(item)"
      >
        {{ itemToString(item) }}
      </div>
      <div class="no-result" v-if="items.length === 0 && inputIsFocused && searched">
        {{$t('no_result')}}
      </div>
    </div>
  </div>
</template>

<script>
  import { Core } from "@/core/Core";

  export default {
    name: "AjaxSelect",

    props: {
      /* model|value must be an object, {id: null} when empty */
      value: {
        type: Object,
        required: true
      },

      /* more query parameters to be sent in every call */
      query: {
        type: Object,
        default: () => {}
      },

      /* class added to the input */
      propClass: {
        default: ''
      },

      /* disabled */
      disabled: {
        type: Boolean,
        default: false
      },

      /* api url */
      api: {
        type: String,
        required: true
      },

      /* mapping function to transform an element to a string */
      mapFn: {
        type: Function,
        required: false
      },

      /* sorting function when receiving items */
      sortFn: {
        type: Function,
        required: false,
      },

      /* allow empty search */
      allowEmptySearch: {
        type: Boolean,
        default: false
      },

      /* array of ignored id, that does not show in result list */
      ignored: {
        type: Array,
        default: () => []
      },

      /* injected style to the selection box */
      selectRowStyle: {
        type: Object,
        default: () => ({})
      },

      /* remove keys from the data of result list */
      removeKeys: {
        type: Array,
        default: null
      },

      placeholder: {
        type: String,
        required: false,
      },

      clearable: {
        type: Boolean,
        required: false,
        default: true
      },

      tryInitOnEmptyData: {
        type: Boolean,
        required: false,
        default: true
      },

      /* Edge case when used by an externalID input */
      edgeCaseExternalIdInput: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        input: '',
        items: [],
        searched: false,
        toDebounce: null,
        focused: null,
        inputIsFocused: false
      }
    },

    computed: {
      placeholderText() {
        return this.placeholder === undefined ? this.$t('search') + '...' : this.placeholder;
      }
    },

    watch: {
      value: {
        immediate: true,
        async handler(value) {
          /* try to init/fill empty data when a non-null ID is sent */
          if (this.tryInitOnEmptyData && value.id && Object.keys(value).length === 1) {
            let response = await this.searchQuery({id: value.id});
            if (response && response[0]) {
              this.onSelection(response[0]);
            } else {
              this.input = `${value.id}`
              if (this.edgeCaseExternalIdInput) {
                this.value.id = null
              }
            }
          } else {
            this.input = this.itemToString(value)
          }
        }
      },

      'value.id'(value) {
        this.$emit('changed', value)
      },

      input(value) {
        this.$emit('changedInput', value)
      }
    },

    methods: {
      searchQuery(query = {}) {
        return Core.noThrowRequest("GET", this.api, Object.assign(query, this.query));
      },

      itemToString(item) {
        if (item && item.id) {
          let tmp = this.mapFn ? this.mapFn(item) : item.name;
          return typeof tmp === 'object' ? this.$to(tmp) : tmp;
        } else {
          return ''
        }
      },

      async onFocusIn() {
        this.inputIsFocused = true;
        if (this.value && this.value.id) {
          this.input = null
        }
        if (this.allowEmptySearch) {
          await this.onTyping({key: null});
        }
      },

      onFocusOut() {
        /* improve this, can't click on search row when not delayed with setTimeout */
        setTimeout(() => {
          this.items = [];
          this.searched = false;
          this.inputIsFocused = false;
          if (this.value && this.value.id) {
            this.setInputWithValue()
          }
        }, 150);
      },

      async onTyping(e = {}) {
        if (!this.api) {
          return;
        }
        if (e.key === 'Enter') {
          if (this.focused !== null) {
            this.onSelection(this.items[this.focused]);
          }
          return;
        }
        if (e.key === 'ArrowUp' && this.items.length) {
          this.focused = this.focused === null && this.focused === 0 ? null : this.focused - 1;
          return;
        }
        if (e.key === 'ArrowDown' && this.items.length) {
          this.focused = Math.min((this.focused || 0) + 1, this.items.length-1);
          return;
        }
        this.debounce(
          300,
          async () => {
            this.searched = false;
            if (!this.input && !this.allowEmptySearch) {
              this.items = [];
              this.focused = null;
              return;
            }
            let response = await this.searchQuery({search: this.input || ''});
            if (response) {
              let results = Array.isArray(response) ? response : response.data;
              if (this.ignored.length > 0) {
                results = results.filter(e => !this.ignored.includes(e.id));
              }
              if (this.sortFn) {
                results.sort(this.sortFn)
              }
              this.items = results;
              this.focused = null;
              this.searched = true;
            }
          }
        );
      },

      onSelection(item) {
        if (this.removeKeys) {
          for (let k of this.removeKeys) {
            delete item[k];
          }
        }

        Object.assign(this.value, item);
        this.items = [];
        this.focused = null;
        this.setInputWithValue();
      },

      clear() {
        for (let k of Object.keys(this.value)) {
          if (k === 'id') {
            this.value.id = null;
          } else {
            delete this.value[k];
          }
        }
        this.setInputWithValue();
      },

      setInputWithValue() {
        this.input = this.itemToString(this.value);
      },

      debounce(delay, func) {
        clearTimeout(this.toDebounce);
        this.toDebounce = setTimeout(() => func(), delay)
      },

      setFocused(index) {
        this.focused = index
      }
    }
  }
</script>

<style lang="scss" scoped>
  $border-color: #d9d9d9;

  .ajax-select {
    position: relative;

    input {
      width: 100%;
    }

    .x {
      position: absolute;
      right: 9px;
      top: 50%;
      transform: translateY(-50%);
      height: 25px;
      width: 25px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 2px;
      cursor: pointer;
      fill: #d9d9d9;

      &:hover {
        fill: var(--primary-color);
      }
    }
    &.open {
      input {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
      .select-box {
        border-top: none;
        border-bottom-right-radius: 8px;
        border-bottom-left-radius: 8px;
      }
    }
  }
  .select-box {
    background-color: white;
    position: absolute;
    border: 1px solid $border-color;
    padding: 10px 10px;
    z-index: 1;
    cursor: pointer;
    width: 100%;
    max-height: 150px;
    overflow-y: scroll;
  }
  .select-box:empty {
    display: none;
  }
  .select-row {
    padding: 8px 16px;
  }
  .focused {
    background-color: var(--primary-color);
    border-radius: 4px;
    color: white;
  }
</style>
